import { SitemapVersionLevelUploadMethod } from "@iventis/domain-model/model/sitemapVersionLevelUploadMethod";
import { EMPTY_GUID } from "@iventis/utilities";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { SitemapVersionLevelStatus } from "@iventis/domain-model/model/sitemapVersionLevelStatus";
import { useSitemapApiContext } from "../context/sitemap-api-context";
import { useSitemapRoutingContext } from "../context/sitemap-routing-context";
import { createEmptySitemapVersionLevel, getSmallestAvailablePositiveIndex, hasSitemapVersionLevelChanged, isSitemapVersionLevelValid } from "../sitemap-helpers";
import { SitemapVersionLevelWithFiles } from "../sitemap-types";
import { useSitemapVersion } from "./use-sitemap-version";
import { useSitemap } from "./use-sitemap";

const SITEMAP_VERSION_LEVEL_KEY = "sitemap-version-level";

/** Handles sitemap version level data fetching and modification */
export function useSitemapVersionLevel() {
    const queryClient = useQueryClient();
    const { getSitemapVersionLevel, createSitemapVersionLevel, updateSitemapVersionLevel, getSitemapVersionLevelCadConfig } = useSitemapApiContext();
    const { getSelectedSitemapId, getSelectedSitemapVersionLevelId, setSelectedSitemapVersionLevelId, getSelectedSitemapVersionId } = useSitemapRoutingContext();
    const { sitemap } = useSitemap();
    const { sitemapVersion } = useSitemapVersion();

    const sitemapId = getSelectedSitemapId();
    const sitemapVersionId = getSelectedSitemapVersionId();
    const sitemapVersionLevelId = getSelectedSitemapVersionLevelId();

    // If sitemap version level id is an empty uuid then we know it is newly created
    const isNewSitemapVersionLevel = sitemapVersionLevelId === EMPTY_GUID;

    // If it is a newly create sitemap version level, we should set the state to be an empty one
    const [sitemapVersionLevel, setSitemapVersionLevel] = useState<SitemapVersionLevelWithFiles>(
        isNewSitemapVersionLevel
            ? createEmptySitemapVersionLevel(sitemapVersionId, getSmallestAvailablePositiveIndex(sitemapVersion?.sitemapVersionLevels))
            : queryClient.getQueryData<SitemapVersionLevelWithFiles>([SITEMAP_VERSION_LEVEL_KEY, sitemapVersionLevelId]) ?? createEmptySitemapVersionLevel(sitemapVersionId, 0)
    );

    const { isLoading, data: savedSitemapVersionLevel, refetch } = useQuery(
        [SITEMAP_VERSION_LEVEL_KEY, sitemapVersionLevelId],
        async () => {
            const sitemapVersionLevelResponse = await getSitemapVersionLevel(sitemapId, sitemapVersionId, sitemapVersionLevelId);
            // If we are or have uploading a cad we also need to get the config for it
            if (sitemapVersionLevelResponse.uploadMethod === SitemapVersionLevelUploadMethod.Cad) {
                const cadConfigResponse = await getSitemapVersionLevelCadConfig(sitemapId, sitemapVersionId, sitemapVersionLevelId);
                const sitemapVersionLevelWithConfig = { ...sitemapVersionLevelResponse, cadConfig: cadConfigResponse };
                setSitemapVersionLevel({
                    ...sitemapVersionLevelWithConfig,
                    perimeter: sitemapVersionLevelWithConfig.perimeter == null ? null : JSON.stringify(sitemapVersionLevelWithConfig.perimeter),
                    status:
                        sitemapVersionLevelWithConfig.status === SitemapVersionLevelStatus.Uploading && sitemapVersionLevel.status === SitemapVersionLevelStatus.Extracting
                            ? SitemapVersionLevelStatus.Extracting
                            : sitemapVersionLevelWithConfig.status,
                });
                return sitemapVersionLevelResponse;
            }
            setSitemapVersionLevel({
                ...sitemapVersionLevelResponse,
                perimeter: sitemapVersionLevelResponse.perimeter == null ? null : JSON.stringify(sitemapVersionLevelResponse.perimeter),
            });
            return sitemapVersionLevelResponse;
        },
        {
            enabled: sitemapVersionLevelId != null && !isNewSitemapVersionLevel,
        }
    );

    const updateMutation = useMutation(
        ({ sitemapVersionLevel, sitemapId }: { sitemapVersionLevel: SitemapVersionLevelWithFiles; sitemapId: string }) => updateSitemapVersionLevel(sitemapVersionLevel, sitemapId),
        {
            onMutate: () => {
                queryClient.setQueryData([SITEMAP_VERSION_LEVEL_KEY, sitemapVersionLevelId], sitemapVersionLevel);
                if (sitemapVersionLevel.uploadMethod === SitemapVersionLevelUploadMethod.Mbtiles) {
                    setSitemapVersionLevel((val) => ({ ...val, style: null, mbtiles: null }));
                } else {
                    setSitemapVersionLevel((val) => ({ ...val, cad: null }));
                }
            },
            retry(failureCount, error: { status?: number }) {
                // Due to eventual consistent storage, it may initially return a 404 if the level can't be found, so we can retry a few times
                if (failureCount < 3 && error?.status === 404) {
                    return true;
                }
                return false;
            },
            retryDelay: 5000,
        }
    );

    const createMutation = useMutation(
        ({ sitemapVersionLevel, sitemapId }: { sitemapVersionLevel: SitemapVersionLevelWithFiles; sitemapId: string }) => createSitemapVersionLevel(sitemapVersionLevel, sitemapId),
        {
            onSuccess: (responseSitemapVersionLevel) => {
                queryClient.setQueryData([SITEMAP_VERSION_LEVEL_KEY, sitemapVersionLevelId], responseSitemapVersionLevel);
                setSelectedSitemapVersionLevelId(responseSitemapVersionLevel.id);
                if (sitemapVersionLevel.uploadMethod === SitemapVersionLevelUploadMethod.Mbtiles) {
                    setSitemapVersionLevel({ ...sitemapVersionLevel, id: responseSitemapVersionLevel.id, status: responseSitemapVersionLevel.status, style: null, mbtiles: null });
                } else {
                    setSitemapVersionLevel({ ...sitemapVersionLevel, id: responseSitemapVersionLevel.id, status: responseSitemapVersionLevel.status, cad: null });
                }
            },
        }
    );

    const isLevelUnique = !sitemapVersion?.sitemapVersionLevels.some(({ levelIndex, id }) => levelIndex === sitemapVersionLevel?.levelIndex && id !== sitemapVersionLevel?.id);

    // Allow saving if there has been an error so the user can retry
    const canSitemapVersionLevelSave =
        sitemapVersionLevel != null &&
        isSitemapVersionLevelValid(sitemapVersionLevel, isNewSitemapVersionLevel) &&
        (hasSitemapVersionLevelChanged(savedSitemapVersionLevel, sitemapVersionLevel) || createMutation.isError || updateMutation.isError) &&
        isLevelUnique;

    const saveSitemapVersionLevel = async (updatedSitemapVersionLevel: SitemapVersionLevelWithFiles) => {
        if (updatedSitemapVersionLevel.id === EMPTY_GUID) {
            const createResponse = await createMutation.mutateAsync({ sitemapVersionLevel, sitemapId });
            return { url: createResponse.fileUploadUrl, id: createResponse.id };
        }
        const updateResponse = await updateMutation.mutateAsync({ sitemapVersionLevel: updatedSitemapVersionLevel, sitemapId });
        return { url: updateResponse, id: sitemapVersionLevel.id };
    };

    const isLevelReassignmentNeeded = () => !isNewSitemapVersionLevel && sitemapVersionLevel.levelIndex !== savedSitemapVersionLevel.levelIndex;

    return {
        sitemap,
        sitemapVersionLevel,
        parentSitemapVersion: sitemapVersion,
        setSitemapVersionLevel,
        isSitemapVersionLevelLoading: isLoading && !isNewSitemapVersionLevel,
        canSitemapVersionLevelSave,
        saveSitemapVersionLevel,
        isSavingSitemapVersionLevel: createMutation.isLoading || updateMutation.isLoading,
        isLevelUnique,
        isLevelReassignmentNeeded,
        refetch,
    };
}
